import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from '@env';
import { MobileStorageService } from './mobile-storage.service';

@Injectable({ providedIn: 'root' })
export class SignalRService {
  private hubConnection: signalR.HubConnection | null = null;

  public isMobileConnected = false;
  public isHandoffEnded = false;
  public isHandoffCompleted = false;
  private isFirstDevice = false;
  private currentTireSpecSessionGuid: string | null = null;
  private currentToken: string | null = null;
  private isListeningLifecycle = false;
  private mobileStorage = inject(MobileStorageService);
  public mobileConnected$ = new Subject<void>();
  public mobileDisconnected$ = new Subject<void>();
  public deviceReplaced$ = new Subject<void>();
  public handoffCompleted$ = new Subject<any>();
  public handoffAcknowledged$ = new Subject<void>();
  public handoffEnded$ = new Subject<void>();

  constructor() {
    this.setupLifecycleListeners();
  }


  public resetState(): void {
    this.isMobileConnected = false;
    this.isHandoffEnded = false;
    this.isHandoffCompleted = false;
    this.currentTireSpecSessionGuid = null;
    this.currentToken = null;
  }

  async startConnection(tireSpecSessionGuid: string, token: string, isFirstDevice: boolean): Promise<void> {
    if (this.currentTireSpecSessionGuid !== tireSpecSessionGuid) {
      this.stopConnection();
      this.resetState();
    }

    this.currentTireSpecSessionGuid = tireSpecSessionGuid;
    this.currentToken = token;
    this.isFirstDevice = isFirstDevice;

    if (this.hubConnection?.state === signalR.HubConnectionState.Connected){
      return;
    }

    const builder = new signalR.HubConnectionBuilder()
        .withUrl(`${environment.config.endpoints.API}/sessionHub`, {
            accessTokenFactory: () => token
          });

    if (!isFirstDevice) {
      builder.withAutomaticReconnect();
    }
    this.hubConnection = builder.build();
    this.registerEvents();
    if (!isFirstDevice) {
      this.setupReconnectHandlers();
    }
    await this.hubConnection.start();
    await this.invoke('JoinSession',isFirstDevice);
  }

  private setupReconnectHandlers() {
    if (!this.hubConnection || this.isFirstDevice) {
      return;
    }
    this.hubConnection.onreconnecting(() => {
      this.mobileDisconnected$.next();
    });

    this.hubConnection.onreconnected(async () => {
      if (this.isHandoffEnded || this.isHandoffCompleted) {
        return;
      }
      await this.invoke('JoinSession',false);
    });

    this.hubConnection.onclose(() => {
      this.mobileDisconnected$.next();
    });
  }

  private registerEvents(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on('HandoffStarted', () => {
        this.isMobileConnected = true;
        this.mobileConnected$.next();
      });

    this.hubConnection.on('DeviceReplaced', () => {
        this.isMobileConnected = false;
        this.isHandoffEnded = true;
        if (this.currentTireSpecSessionGuid) {
          this.mobileStorage.clearState(this.currentTireSpecSessionGuid);
        }
        this.stopConnection();
        this.deviceReplaced$.next();
      });

    this.hubConnection.on('HandoffDisconnected', () => {
        this.isMobileConnected = false;
        this.mobileDisconnected$.next();
      });

    this.hubConnection.on('HandoffEnded', () => {
        this.isHandoffEnded = true;
        this.isMobileConnected = false;
        this.handoffEnded$.next();
      });

    this.hubConnection.on('HandoffCompleted', (data: any) => {
      this.isHandoffCompleted = true;
      if (this.currentTireSpecSessionGuid) {
        this.mobileStorage.clearState(this.currentTireSpecSessionGuid);
      }
      let parsedData = data;
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch (e) {
          console.error('Error parsing handoff data', e);
        }
      }
      this.handoffCompleted$.next(parsedData);
    });

    this.hubConnection.on('HandoffAcknowledged', () => {
        this.handoffAcknowledged$.next();
      });
  }

  private setupLifecycleListeners() {
    if (typeof window === 'undefined' || this.isListeningLifecycle) {
      return;
    }

    this.isListeningLifecycle = true;
    const handler = () => {
      if (document.visibilityState === 'visible') {
        this.checkAndReconnect();
      }
    };

    window.addEventListener('visibilitychange',handler);
    window.addEventListener('focus',handler);
    window.addEventListener('pageshow',handler);
  }


  async checkAndReconnect() {
    if (this.isFirstDevice) {
      return;
    }

    if (this.isHandoffEnded || this.isHandoffCompleted) {
      return;
    }

    if (!this.currentTireSpecSessionGuid || !this.currentToken) {
      return;
    }

    if (!this.hubConnection || this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
      await this.startConnection(this.currentTireSpecSessionGuid,this.currentToken,false);
    }
  }

  async completeHandoff(data: any)
  {
    await this.invoke('CompleteHandoff', {handoffData: JSON.stringify(data)});
  }
  async acknowledgeHandoff() {
    await this.invoke('AcknowledgeHandoff');
  }

  stopConnection() {
    this.hubConnection?.stop();
    this.hubConnection = null;
  }

  private async invoke<T = void>(methodName: string, ...args: unknown[]): Promise<T> {
    if (!this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) {
      throw new Error("SignalR connection unavailable");
    }
 
    try {
      return await this.hubConnection.invoke<T>(methodName, ...args);
    } catch (err) {
      throw new Error(this.extractErrorMessage(err));
    }
  }

  private extractErrorMessage(err: unknown): string {
    const raw = err instanceof Error ? err.message : String(err);
    const marker = 'HubException: ';
    const idx = raw.lastIndexOf(marker);
    return idx >= 0 ? raw.slice(idx + marker.length).trim() : raw.trim();
  }
}
